import { ref, computed, onMounted, watch } from 'vue'
import { matchFormat } from '@touchvue/utils'
import { isString, isDateObj } from '@touchvue/utils'
import { DatePickerType, DatePickerValue, getDefaultFormat } from './date-picker'

export interface UseDatePickerOptions {
	modelValue: DatePickerValue
	value?: DatePickerValue
	type: DatePickerType
	multiple?: boolean
	valueFormat: string
	displayFormat: string
	min?: string | Date | number
	max?: string | Date | number
	beforeChange?: (value: string) => boolean | Promise<boolean>
}

export interface UseDatePickerEmits {
	(e: 'update:modelValue', value: DatePickerValue): void
	(e: 'change', value: DatePickerValue): void
	(e: 'beforeChange', value: DatePickerValue): void
}

export function addZero(str: string | number) {
	return ('00' + str).substr(str.toString().length)
}

export function getDateStrFromMatch(regExp: RegExp, value = '', format = 'yyyy-MM-dd') {
	const yMatch = format.match(regExp)
	if (!yMatch || yMatch.index === undefined) return ''
	return value.substr(yMatch.index, yMatch[0].length)
}

export function getDateFromValueFunc(props: { type: string }, value: string | Date, format: string = 'yyyy-MM-dd'): Date {
	if (!value) return new Date()
	if (typeof value === 'string') {
		const date = new Date()
		let day: string | number
		if (props.type === 'month') {
			day = 1
		} else {
			day = getDateStrFromMatch(/d+/, value, format) || addZero(date.getDate())
		}
		let datearr = [getDateStrFromMatch(/y+/, value, format) || date.getFullYear(), getDateStrFromMatch(/M+/, value, format) || addZero(date.getMonth() + 1), day]
		let timearr = [getDateStrFromMatch(/H+|h+/, value, format) || addZero(date.getHours()), getDateStrFromMatch(/m+/, value, format) || addZero(date.getMinutes()), getDateStrFromMatch(/s+/, value, format) || addZero(date.getSeconds())]
		if (props.type === 'time') {
			datearr = [date.getFullYear(), addZero(date.getMonth() + 1), addZero(date.getDate())]
			const times = value.split(':')
			timearr = [times[0] || addZero(date.getHours()), times[1] || addZero(date.getMinutes()), times[2] || addZero(date.getSeconds())]
		}
		value = new Date(`${datearr.join('/')} ${timearr.join(':')}`)
	}
	return value
}

export function formatDateFunc(props: { type: string }, date: Date, fmt?: string): string {
	if (date) {
		if (!fmt) {
			if (props.type === 'datetime') {
				fmt = 'yyyy-MM-dd HH:mm:ss'
			} else if (props.type === 'time') {
				fmt = 'HH:mm:ss'
			} else {
				fmt = 'yyyy-MM-dd'
			}
		}
		if (/(y+)/.test(fmt)) {
			fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length))
		}
		const o: { [key: string]: number } = {
			'M+': date.getMonth() + 1,
			'd+': date.getDate(),
			'H+': date.getHours(),
			'm+': date.getMinutes(),
			's+': date.getSeconds()
		}
		for (const k in o) {
			if (new RegExp('(' + k + ')').test(fmt)) {
				const str = o[k] + ''
				fmt = fmt.replace(RegExp.$1, RegExp.$1.length === 1 ? str : addZero(str))
			}
		}
		return fmt
	} else {
		return ''
	}
}

export function useDatePicker(props: UseDatePickerOptions, emit: UseDatePickerEmits) {
	// 当前选择的Date
	const date = ref<DatePickerValue>('')
	const iValue = ref<DatePickerValue>('')
	// 当前显示的日期
	const datedisplay = ref('')
	const isDateType = ref<boolean>()

	// 计算属性
	const currentValue = computed(() => props.modelValue || props.value)

	const displayFormatRegExp = computed(() => {
		const format = props.displayFormat
		const regexStr = new RegExp(
			`^${String(format)
				.replace(/-|\//g, '\\-')
				.replace(/[a-zA-Z]/g, '\\d')}$`
		)
		return new RegExp(regexStr)
	})

	const valueFormat = computed(() => {
		if (props.valueFormat) {
			return props.valueFormat
		}
		return getDefaultFormat(props.type)
	})

	const displayFormat = computed(() => {
		if (props.displayFormat) {
			return props.displayFormat
		}
		return getDefaultFormat(props.type)
	})

	/**
	 * 从字符串或Date对象中获取Date对象
	 * @param {string | Date} value - 输入的日期值，可以是字符串或Date对象
	 * @param {string} format - 日期格式，默认为'yyyy-MM-dd'
	 * @returns {Date} 解析后的Date对象，如果输入为空则返回当前日期
	 */
	function getDateFromValue(value: string | Date, format: string = 'yyyy-MM-dd'): Date {
		return getDateFromValueFunc(props, value, format)
	}

	/**
	 * 从Date对象转化为字符串
	 * @param {Date} date - 输入的日期值，必须是Date对象
	 * @param {string} fmt - 日期格式
	 * @returns {string} 转换后的字符串
	 */
	function formatDate(date: Date, fmt?: string): string {
		return formatDateFunc(props, date, fmt)
	}

	function emitChangeEvent() {
		let value = iValue.value
		// v-model为Date对象时
		if (isDateType.value && isString(value)) {
			value = getDateFromValue(value, props.valueFormat)
		}
		emit('change', value)
		emit('update:modelValue', value)
	}

	function handleDisplayChange(value: string) {
		datedisplay.value = value
		emit('change', datedisplay.value)
	}

	function handleDisplayBlur() {
		setTimeout(() => {
			if (!datedisplay.value || (isNaN(new Date(datedisplay.value).getTime()) && !(props.multiple && (props.type === 'month' || props.type === 'date')))) {
				datedisplay.value = ''
				changeValue('')
			} else {
				emit('beforeChange', datedisplay.value)
				if (!displayFormatRegExp.value.test(datedisplay.value) && !(props.multiple && (props.type === 'month' || props.type === 'date'))) {
					const inputValue = datedisplay.value
					datedisplay.value = formatDate(new Date(inputValue), displayFormat.value)
					iValue.value = formatDate(new Date(inputValue), valueFormat.value)
					emitChangeEvent()
				} else if (props.multiple && (props.type === 'month' || props.type === 'date')) {
					changeValue(datedisplay.value)
				} else {
					changeValue(getDateFromValue(datedisplay.value, displayFormat.value))
				}
			}
		}, 100)
	}

	function changeValue(value: string | Date) {
		emit('beforeChange', value)
		if (!value) {
			cleanValue()
		} else if (props.type === 'time') {
			if (typeof value === 'string') {
				datedisplay.value = value.split(' ').length > 1 ? value.split(' ')[1] : value
				iValue.value = value.split(' ').length > 1 ? value.split(' ')[1] : value
			} else {
				let valueStr = formatDate(new Date(value), 'yyyy-MM-dd HH:mm:ss')
				const flag = isNaN(new Date(valueStr).getTime())
				if (flag) {
					valueStr = valueStr.replace(/-/g, '/')
				}
				datedisplay.value = valueStr.split(' ').length > 1 ? valueStr.split(' ')[1] : valueStr
				iValue.value = datedisplay.value
			}
			emitChangeEvent()
		} else if (props.multiple && (props.type === 'month' || props.type === 'date')) {
			// const valueStr = formatDate(new Date(value), 'yyyy-MM')
			datedisplay.value = value as string
			iValue.value = value
			emitChangeEvent()
		} else {
			let valueStr = typeof value === 'string' ? value : formatDate(new Date(value), valueFormat.value)
			const flag = isNaN(new Date(valueStr).getTime())
			if (flag && typeof valueStr === 'string') {
				valueStr = valueStr.replace(/-/g, '/')
			}
			datedisplay.value = formatDate(new Date(valueStr), displayFormat.value)
			iValue.value = formatDate(new Date(valueStr), valueFormat.value)
			emitChangeEvent()
		}
	}

	function cleanValue() {
		const oldVal = iValue.value
		iValue.value = ''
		if (oldVal) {
			emit('change', iValue.value)
			emit('update:modelValue', iValue.value)
		}
	}

	watch(
		() => props.modelValue,
		val => {
			if (isDateObj(props.modelValue)) {
				isDateType.value = true
			}
			if (val === '' || val === null || val === undefined) {
				date.value = ''
				datedisplay.value = ''
			} else {
				// let newValue = typeof val === 'string' ? val.trim().substring(0, valueFormat.value.length) : val

				let newValue = props.multiple && (props.type === 'month' || props.type === 'date') ? val : typeof val === 'string' ? val.trim().substring(0, valueFormat.value.length) : val

				if (typeof newValue === 'string' && !matchFormat(newValue, valueFormat.value) && !(props.multiple && (props.type === 'month' || props.type === 'date'))) {
					return
				}
				if (props.type === 'time') {
					date.value = getDateFromValue(newValue, 'yyyy-MM-dd HH:mm:ss')
					datedisplay.value = formatDate(date.value, displayFormat.value)
				}
				if (props.multiple && (props.type === 'month' || props.type === 'date')) {
					date.value = newValue
					datedisplay.value = newValue as string
				} else {
					newValue = getDateFromValue(newValue, valueFormat.value)
					date.value = newValue
					datedisplay.value = formatDate(newValue, displayFormat.value)
				}
			}
		},
		{ immediate: true }
	)

	// 生命周期
	onMounted(() => {
		if (currentValue.value) {
			iValue.value = currentValue.value
			emit('change', currentValue.value)
		}
	})

	return {
		// 响应式状态
		date,
		datedisplay,
		// 核心方法
		changeValue,
		cleanValue, // 重命名为 cleanValue
		handleDisplayChange,
		handleDisplayBlur,
		emitChangeEvent
	}
}
